import { Button, Chip, addToast, cn } from "@heroui/react";
import { Icon } from "@iconify/react";
import Dropzone, {
	type DropzoneProps,
	type FileRejection,
} from "react-dropzone";

import { FILE_SIZE_UPLOAD_LIMIT } from "@onesolution/constants";
import { useFieldContext, useLanguage } from "@onesolution/hooks";
import type { TranslationsKeys } from "@onesolution/hooks/useLanguage";
import type { MONDAY_TYPES } from "@onesolution/monday";
import { useEffect, useState } from "react";

type FilePickerProps = {
	label: string;
	withPreview?: boolean;
	multiple?: boolean;
	cantDeleteExisting?: boolean;
	labelKey?: TranslationsKeys;
	onFileRemoved?: (toRemove: MONDAY_TYPES["file"][number]) => unknown;
};

function isSameFile(fileA: File, fileB: File) {
	return fileA.size === fileB.size && fileA.name === fileB.name;
}

//Todo : why do i need this, useEffect is triggering twice
let isSet = false;
let _dragState = 0;

export function FilePicker({
	withPreview = true,
	label,
	onFileRemoved,
	cantDeleteExisting,
	labelKey = "file.attach-files",
	...props
}: FilePickerProps & DropzoneProps) {
	const field = useFieldContext<File[]>();
	const { t } = useLanguage();
	const [dragState, setDragState] = useState(0);

	const handleDrop = (files: File[], rejects: FileRejection[]) => {
		_dragState = 0;
		setDragState(_dragState);
		if (rejects.length) {
			addToast({
				title: t("file.error"),
				description: rejects
					.flatMap(({ errors }) =>
						errors.map((error) =>
							t(`file.errors.${error.code}` as TranslationsKeys),
						),
					)
					.join(", "),
				color: "danger",
			});
		}
		const toAdd = files.filter(
			(toAdd) => !field.state.value?.some((file) => isSameFile(file, toAdd)),
		);
		field.handleChange(
			props.multiple ? [...(field.state.value || []), ...toAdd] : toAdd,
		);
	};

	const handleRemoveFile = (toRemove: File | MONDAY_TYPES["file"][number]) => {
		if (!(toRemove instanceof File)) onFileRemoved?.(toRemove);
		field.handleChange(
			field.state.value.filter((file) => !isSameFile(file, toRemove as File)),
		);
	};

	useEffect(() => {
		if (!isSet) {
			const handleDragEnter = () => {
				_dragState++;
			};

			const handleDragLeave = () => {
				_dragState--;
				setDragState(_dragState);
			};

			window.document.addEventListener("dragenter", handleDragEnter, true);
			window.document.addEventListener("dragleave", handleDragLeave, true);

			isSet = true;

			return () => {
				window.document.removeEventListener("dragenter", handleDragEnter, true);
				window.document.removeEventListener("dragleave", handleDragLeave, true);
				isSet = false;
			};
		}
	}, []);

	return (
		<Dropzone onDrop={handleDrop} maxSize={FILE_SIZE_UPLOAD_LIMIT} {...props}>
			{({ getRootProps, getInputProps, isDragActive, open }) => (
				<div>
					<div
						className={cn(
							"absolute inset-0 transition-opacity duration-150",
							isDragActive ? "opacity-95 z-20" : "opacity-0",
							dragState > 0 ? "" : "pointer-events-none",
						)}
						{...getRootProps()}
						onClick={() => null}
					>
						<div className="bg-success-300 opacity-85 flex items-center justify-center w-full h-full" />
						<Icon
							className="animate-bounce text-background text-8xl absolute top-1/3 left-1/2 -translate-x-1/2  pointer-events-none"
							icon="solar:file-download-bold"
						/>
					</div>
					<div
						className={cn(
							"-left-2 relative z-20 rounded-lg p-2 flex flex-col gap-2",
							isDragActive && "shadow-lg pointer-events-none bg-background",
						)}
					>
						{label && <h4>{label}</h4>}
						<input className="w-fit" {...getInputProps()} />
						<Button
							variant="bordered"
							className="w-fit bg-background"
							onPress={open}
							startContent={<Icon icon="solar:file-send-bold" />}
						>
							{t(labelKey)}
						</Button>
						{withPreview && field.state.value?.length > 0 && (
							<div className="flex gap-2 flex-wrap">
								{field.state.value.map((file) => (
									<Chip
										variant="flat"
										endContent={
											file instanceof File || !cantDeleteExisting ? (
												<Icon
													onClick={() => handleRemoveFile(file)}
													className="cursor-pointer ml-2"
													icon="solar:close-circle-outline"
												/>
											) : null
										}
										key={`${file.size}_${file.name}`}
									>
										{file.name}
									</Chip>
								))}
							</div>
						)}
					</div>
				</div>
			)}
		</Dropzone>
	);
}
